import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import {
  allowNoPayments,
  FlexCardDisplay,
  getPoliciesData,
  InvoiceCard,
  PurchaseReceipt,
  PurchaseReceiptSkeleton,
  Spacer,
} from 'components-ui'
import { Hooks } from 'service-api'
import { PurchaseSummarySkeleton } from '../purchase_summary/purchase_summary_skeleton'
import { useRoutes } from 'app/use_routes'
import { isUserLoggedIn } from '@providers/session/helpers'
import { useSessionContext } from '@providers/session'
import { newPetRecord } from '@providers/booking/helper'

export const BookingConfirmation = () => {
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const { accountName = '', orderId = '', serviceTypeName = 'boarding' } = useParams()
  const theme = useTheme()
  const renderStyle = useMediaQuery(theme.breakpoints.down('md')) ? 'mobile' : 'desktop'
  const isMobile = renderStyle === 'mobile'

  const {
    data: receipt,
    isFetching: isReceiptLoading,
    refetch: refetchReceipt,
    error: receiptError,
  } = Hooks.useClientReceipt(accountName, orderId)
  const {
    data: order,
    isFetching: isOrderLoading,
    refetch: refetchOrder,
    error: orderError,
  } = Hooks.useClientOrder(accountName, orderId)
  const noPaymentsAllowed = allowNoPayments(order?.primaryServiceType?.locationBookingConf?.[0])
  const policyCardsData = useMemo(
    () => (receipt ? getPoliciesData(receipt, noPaymentsAllowed) : []),
    [receipt, noPaymentsAllowed]
  )

  const configs = Hooks.useClientBookingConfigs(accountName, serviceTypeName)
  const location = Hooks.useClientLocation(
    accountName,
    configs.data?.species?.[0]?.locationId ||
      configs.data?.petCount?.[0]?.locationId ||
      configs.data?.period?.[0]?.locationId ||
      ''
  )

  useEffect(() => {
    bookingCtx.dispatch({
      type: 'SET_SEARCH_STATE',
      payload: { value: 'paymentSummary' },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.currentState])

  useEffect(() => {
    if (location.data) {
      bookingCtx.dispatch({
        type: 'SET_LOCATION_CONFIG',
        payload: { value: location.data },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.config.location, location.data])

  const isPetNameInCtx = !!bookingCtx.state?.groups[0]?.pets[0]?.name
  useEffect(() => {
    // confirmation reload - use pet values from receipt if there are no pet names defined in the booking state
    if (receipt && !isPetNameInCtx) {
      bookingCtx.dispatch({
        type: 'ADD_CONFIRMATION_GROUPS',
        payload: {
          receipt: receipt,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt, isPetNameInCtx])

  // if they do have an account but aren't logged in, prompt to log in
  if (
    !isUserLoggedIn(sessionCtx.state.user) &&
    receiptError?.response?.data?.error === 'Order does not belong to user'
  ) {
    navigateRoute(
      'signIn',
      {},
      {
        state: {
          redirect: {
            route: 'paymentSummary',
            params: { orderId },
          },
        },
      }
    )
  }

  if (receiptError?.response?.data?.error === 'No Order found') {
    navigateRoute('notFound', {})
  }

  if (isOrderLoading || isReceiptLoading) {
    return <PurchaseSummarySkeleton />
  }

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Typography variant="h1" sx={{ px: 2 }}>
        {t('paymentSummary.title')}
      </Typography>

      <Spacer height={3} />

      <Stack direction="column" spacing={3} flex={1} width="100%">
        {order &&
          (order.invoices || []).map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              order={order}
              onCancelInvoice={() => {
                refetchOrder()
                refetchReceipt()
              }}
            />
          ))}
      </Stack>

      <Spacer height={3} />

      <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={3} flex={1}>
        <Stack spacing={3} sx={{ flex: 1 }} width="100%">
          {policyCardsData.map((group, index) => (
            <FlexCardDisplay key={index} cardData={group} />
          ))}
        </Stack>
        <Stack direction="column" spacing={2} flex={1} mt={isMobile ? 3 : 0} width="100%">
          <Box sx={{ flex: 1 }}>
            {isReceiptLoading ? <PurchaseReceiptSkeleton /> : <PurchaseReceipt receipt={receipt} />}
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}
